import {
  Content,
  ContentHeader,
  Header,
  Page,
  Progress,
  SupportButton,
} from '@backstage/core-components';
import { Box, Button, Grid, TextField, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import React, { ReactNode, useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';
import axios from 'axios';
import { microsoftAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { useGetAPI } from '../../Utils/useGetAPI';

const getColor = props => {
  if (props.isDragAccept) {
    return '#00e676';
  }
  if (props.isDragReject) {
    return '#ff1744';
  }
  if (props.isFocused) {
    return '#2196f3';
  }
  return '#9C9B9B';
};

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  border-width: 2px;
  border-radius: 2px;
  border-color: ${props => getColor(props)};
  border-style: dashed;
  background-color: #fafafa;
  color: #bdbdbd;
  outline: none;
  transition: border 0.24s ease-in-out;
`;

interface FileForUpload {
  filesToSend: Array<File> | null;
}

export const StructurizrApp = () => {
  const identityAPI = useApi(microsoftAuthApiRef);
  const [fileToUpload, setFileToUpload] = useState<FileForUpload>({
    filesToSend: null,
  });
  const [translatedDSL, setTranslatedDSL] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const url = useGetAPI();
  const onDrop = useCallback(acceptedFile => {
    setFileToUpload({
      filesToSend: acceptedFile,
    });
  }, []);

  const {
    acceptedFiles,
    fileRejections,
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    maxFiles: 4,
    accept: {
      'text/*': [
        '.pu',
        '.txt',
        '.html',
        '.java',
        '.wsd',
        '.c',
        '.cpp',
        '.tex',
        '.apt',
        '.doc',
      ],
    },
    onDrop,
  });

  const alertNotification = (severityColor: string, alertText: ReactNode) => {
    return (
      <Alert
        style={{ marginBottom: '10px%', marginTop: '10px' }}
        severity={severityColor}
      >
        {alertText}
      </Alert>
    );
  };

  const acceptedFileItems = acceptedFiles.map(file => (
    <>
      <Typography style={{ marginTop: '5%' }}>File(s) Uploaded</Typography>
      <li key={file.path}>
        {file.path} - {file.size} bytes
      </li>
    </>
  ));

  const fileRejectionItems = fileRejections.map(({ file, errors }) => (
    <>
      {errors.length >= 1
        ? alertNotification(
            'error',
            <>
              Your upload has issues. Please address the errors below.
              <li key={file.path}>
                {file.path} - {file.size} bytes
                <ul>
                  {errors.map(e => (
                    <li key={e.code}>{e.message}</li>
                  ))}
                </ul>
              </li>
            </>,
          )
        : null}
    </>
  ));

  const renderDSL = () => {
    return (
      <>
        <Button
          style={{ marginBottom: '5px' }}
          fullWidth
          onClick={() => {
            navigator.clipboard.writeText(translatedDSL);
          }}
        >
          Copy to Clipboard
        </Button>
        <TextField
          style={{ marginTop: '5px' }}
          minRows={4}
          multiline
          value={
            translatedDSL.length > 1
              ? translatedDSL
              : 'Your DSL text will appear here after clicking submit.'
          }
          variant="outlined"
          fullWidth
          InputProps={{
            readOnly: translatedDSL.length > 1 ? false : true,
          }}
        />
      </>
    );
  };

  async function sendFilesToAPI() {
    setLoading(true);
    const token = await identityAPI.getIdToken();

    const fileArr = fileToUpload.filesToSend;

    const formData = new FormData();

    fileArr.forEach(file => {
      formData.append('files', file);
    });

    axios
      .post(`${url}/v1/api/plantuml/convert`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Basic ${token}`,
        },
      })
      .then(response => {
        setLoading(false);
        setError(false);
        setTranslatedDSL(response.data);
      })
      .catch(_ => {
        setLoading(false);
        setError(true);
      });
  }

  const loadingBar = () => {
    if (loading) {
      return <Progress />;
    }
    return null;
  };

  const submitError = () => {
    if (error === true) {
      return alertNotification(
        'error',
        <>
          There was an issue reaching the server. Please wait a few minutes and
          try again.
        </>,
      );
    }

    return null;
  };

  return (
    <Page themeId="tool">
      <Header title="Structurizr Translator" />
      <Content>
        <ContentHeader title="">
          <SupportButton>
            <Typography variant="subtitle1" gutterBottom>
              Use this tool to translate PlantUML diagrams.
            </Typography>
          </SupportButton>
        </ContentHeader>

        <Box sx={{ flexGrow: 1 }}>
          <Grid container style={{ marginTop: '5%' }} spacing={2}>
            <Grid item xs={5}>
              <Grid item xs={12} spacing={0}>
                <Typography style={{ marginBottom: '5%' }}>
                  Use the File Upload Manager below
                </Typography>
              </Grid>
              <Grid item xs={12} spacing={0}>
                <Container
                  {...getRootProps({ isFocused, isDragAccept, isDragReject })}
                >
                  <input {...getInputProps()} />
                  <p>Drag or Click Here to Upload your PUML File.</p>
                </Container>
              </Grid>
              <Grid item xs={12} spacing={0}>
                <div>
                  {acceptedFileItems}
                  {fileRejectionItems}
                </div>
              </Grid>
              <Grid item xs={12}>
                <div>
                  <Button
                    disabled={
                      fileRejections.length !== 0 ||
                      acceptedFileItems.length === 0 ||
                      loading
                    }
                    onClick={e => {
                      sendFilesToAPI();
                    }}
                    style={{
                      marginLeft: '80%',
                      marginTop: '10px',
                      marginBottom: '5px',
                    }}
                  >
                    Submit
                  </Button>

                  {submitError()}

                  {loadingBar()}
                </div>
              </Grid>
            </Grid>
            <Grid item xs={1} />
            <Grid item xs={6}>
              {renderDSL()}
            </Grid>
          </Grid>
        </Box>
      </Content>
    </Page>
  );
};
